/**
 * The Connect tab's Delivery log — what actually got sent, and whether it
 * arrived.
 *
 * Visual Reference: `design/screens/editor-connect.html`, the **Delivery log**
 * card. Three columns: what went where, when, and how it went.
 *
 * PRD §3.10: "Delivery log for every notification. Internal recipients only."
 *
 * IT READS `deliveries`, NOT `events` (DECISION-006). The operator's question
 * is "did it arrive", not "what happened" — a log built on the audit trail
 * would show four rows for a delivery that failed three times and then
 * succeeded, and the answer to the question asked is the last one.
 *
 * Story 9.5.
 */
import { useCallback, useEffect, useState } from 'react'
import { ApiFailure, apiGet } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Pill } from '../ui/Pill'
import { Skeleton } from '../ui/Skeleton'

type Delivery = {
  id: string
  service: string
  destination: string
  leadName: string | null
  leadId: string | null
  createdAt: number
  status: 'Delivered' | 'Failed' | 'Sending'
  error: string | null
}

type View = { deliveries: Delivery[]; nextCursor?: string }

/** The export's own empty copy — a log with nothing in it is not a failure. */
export const EMPTY_LOG = 'Nothing has been sent yet. Sends appear here as they happen.'

const TONE = { Delivered: 'hot', Failed: 'error', Sending: 'neutral' } as const

/**
 * "Today, 09:12" · "Yesterday, 17:30" · "Monday, 08:00" — the mockup's own
 * three forms, in that order of recency.
 *
 * The BROWSER's timezone, deliberately, and it is the one place in the product
 * that is right: the digest scheduler evaluates in the FUNNEL's zone because a
 * send time is a promise about the operator's morning (RULE-034), while this is
 * a person reading a screen and "09:12" means the clock on their wall.
 */
export function whenLabel(at: number, now: number): string {
  const then = new Date(at)
  const time = `${then.getHours().toString().padStart(2, '0')}:${then
    .getMinutes()
    .toString()
    .padStart(2, '0')}`

  const midnight = new Date(now)
  midnight.setHours(0, 0, 0, 0)
  const days = Math.floor((midnight.getTime() - then.getTime()) / 86_400_000) + 1

  if (days <= 0) return `Today, ${time}`
  if (days === 1) return `Yesterday, ${time}`
  if (days < 7) {
    return `${then.toLocaleDateString(undefined, { weekday: 'long' })}, ${time}`
  }
  return `${then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${time}`
}

/**
 * What the first column says.
 *
 * The mockup's own idiom: two facts joined by `·` — "GoHighLevel · Northside",
 * "Weekly digest · 18 leads". Story 9.5's criterion asks for "service, lead
 * name, timestamp and Delivered/Failed", which is four things in three columns,
 * and this is where the export already puts two.
 *
 * The lead name comes FIRST because it is what an operator is scanning for.
 * Absent on a digest, which is about a band rather than one lead — and absent
 * for a viewer with no PII grant, because the name came through `redactLead`
 * like every other lead field.
 */
export function rowLabel(delivery: Delivery): string {
  if (delivery.leadName) return `${delivery.leadName} · ${delivery.destination}`
  if (!delivery.leadId) return `${delivery.service} digest · ${delivery.destination}`
  return delivery.destination
}

export function DeliveryLog({ savedAt }: { savedAt?: unknown }) {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [view, setView] = useState<View | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [now] = useState(() => Date.now())

  const load = useCallback(async () => {
    if (!workspaceId) return
    setError(null)
    try {
      setView(await apiGet<View>(`/v1/w/${workspaceId}/deliveries`))
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't load the delivery log. Sends are still happening — try again.",
      )
    }
  }, [workspaceId])

  useEffect(() => {
    void load()
  }, [load, savedAt])

  return (
    <section
      aria-label="Delivery log"
      className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Delivery log</span>
        {view && view.deliveries.length > 0 ? (
          <span className="text-mdx-13 text-mdx-slate">
            The most recent {view.deliveries.length === 1 ? 'send' : `${view.deliveries.length} sends`}
          </span>
        ) : null}
      </div>

      {error ? (
        <>
          <Callout>{error}</Callout>
          <Button size="small" variant="secondary" className="self-start" onClick={() => void load()}>
            Try again
          </Button>
        </>
      ) : !view ? (
        <div className="flex flex-col gap-mdx-2">
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} className="h-mdx-input" />
          ))}
        </div>
      ) : view.deliveries.length === 0 ? (
        <p className="m-0 max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">{EMPTY_LOG}</p>
      ) : (
        <div className="flex flex-col gap-mdx-2">
          {view.deliveries.map((delivery) => (
            <div
              key={delivery.id}
              className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-10 rounded-mdx-control px-mdx-16 py-mdx-14 transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground"
            >
              <span className="min-w-0 flex-1 basis-mdx-row-identity truncate text-mdx-15 text-mdx-ink">
                {rowLabel(delivery)}
              </span>
              <span className="shrink-0 text-mdx-13 tabular-nums text-mdx-slate">
                {whenLabel(delivery.createdAt, now)}
              </span>
              <Pill tone={TONE[delivery.status]} dot className="shrink-0">
                {delivery.status}
              </Pill>
            </div>
          ))}
        </div>
      )}

      {/*
        The reason a failure gives, under the rows rather than inside one. The
        mockup's error state draws "a failed-dispatch banner in plain words with
        a link into the delivery log. No status codes" — this is the log itself,
        so the words go here and the row above stays scannable.
      */}
      {(view?.deliveries ?? [])
        .filter((delivery) => delivery.status === 'Failed' && delivery.error)
        .slice(0, 1)
        .map((delivery) => (
          <p key={delivery.id} className="m-0 max-w-mdx-measure text-mdx-13 text-mdx-slate">
            The last failure was to {delivery.destination} — {delivery.error}. Nothing about the
            lead is affected; it is captured, scored and in your leads list either way.
          </p>
        ))}
    </section>
  )
}
